/**
 * CONNECTED ACCOUNTS (Settings depth). Linked bank accounts, M-Pesa, and external wallets, used for
 * deposits, withdrawals, and settlement. An external wallet must be verified before it can receive.
 */

import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';

export default function ConnectedAccounts() {
  const { data, loading, error, reload } = useResource(() => api.connectedAccounts(), []);

  if (loading)
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <LoadingCard lines={2} />
        <LoadingCard lines={3} />
      </div>
    );
  if (error) return <ErrorState body="We could not load your accounts." onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Settings
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Connected accounts</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Banks, M-Pesa, and external wallets.</p>
      </header>

      <Group title="Bank accounts" addLabel="Add a bank">
        {data.banks.map((b) => (
          <Row key={b.id} title={b.name} sub={b.masked} badge={b.primary ? 'Primary' : undefined} />
        ))}
      </Group>

      <Group title="M-Pesa" addLabel="Add a number">
        {data.mpesa.map((m) => (
          <Row key={m.id} title={m.number} sub="M-Pesa" verified={m.verified} />
        ))}
      </Group>

      <Group title="External wallets" addLabel="Link a wallet">
        {data.wallets.map((w) => (
          <Row key={w.id} title={w.network} sub={w.address} verified={w.verified} mono />
        ))}
      </Group>

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs text-ink-muted">
        A withdrawal only goes to a verified destination. Verifying proves the account is really
        yours.
      </p>
    </div>
  );
}

function Group({
  title,
  addLabel,
  children,
}: {
  title: string;
  addLabel: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-semibold text-ink">{title}</h2>
        <button className="text-sm font-medium text-brand hover:underline">+ {addLabel}</button>
      </div>
      <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
        {children}
      </ul>
    </section>
  );
}

function Row({
  title,
  sub,
  badge,
  verified,
  mono,
}: {
  title: string;
  sub: string;
  badge?: string;
  verified?: boolean;
  mono?: boolean;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className={`truncate text-xs text-ink-subtle ${mono ? 'font-mono' : ''}`}>{sub}</p>
      </div>
      {badge ? (
        <span className="rounded-pill bg-primary/10 px-2 py-0.5 text-micro font-medium text-brand">
          {badge}
        </span>
      ) : null}
      {verified !== undefined ? (
        <span
          className={`rounded-pill px-2 py-0.5 text-micro font-medium ${verified ? 'bg-accent/15 text-accent' : 'bg-warning/15 text-warning'}`}
        >
          {verified ? 'Verified' : 'Verify'}
        </span>
      ) : null}
    </li>
  );
}
